import { createUniqueId, deepEquals, exists } from '@jack-henry/frontend-utils/functions';
import '@vaadin/select';
// eslint-disable-next-line no-duplicate-imports
import type {
    Select as VaadinSelect,
    SelectValueChangedEvent as VaadinSelectChangedEvent,
    SelectItem as VaadinSelectItem,
} from '@vaadin/select';
import { css, html, PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { ref } from 'lit/directives/ref.js';
import { SelectItemVm } from './forms';
import { TmInputFieldBase } from './tm-input-field.base';

export const tagName = 'tm-select';
@customElement(tagName)
export class TmSelect<T> extends TmInputFieldBase<T> {
    @property({ attribute: false })
    public items: SelectItemVm<T>[] = [];

    public get selectedItem() {
        const vaadinSelect = this.selectRef;

        if (!vaadinSelect) {
            return undefined;
        }

        const selectedId = vaadinSelect.value;
        const item = this.itemMap.get(selectedId);

        if (!exists(item)) {
            return undefined;
        }

        return item;
    }

    @state()
    private vaadinItems?: VaadinSelectItem[];

    @state()
    private vaadinInitial?: string;

    /**
     * A map of IDs to select items passed in by the consumer.
     *
     * This is necessary to support rich objects externally while
     * internally the underlying <vaadin-select> only supports strings.
     */
    private itemMap = new Map<string, SelectItemVm<T>>();

    private selectRef?: VaadinSelect;

    public disconnectedCallback() {
        this.itemMap.clear();
    }

    protected updated(changedProps: PropertyValues): void {
        if (changedProps.has('items')) {
            this.populateVaadinItems(changedProps.get('items'));
        }

        if (changedProps.has('initial')) {
            this.populateVaadinInitial(changedProps.get('initial'));
        }
    }

    protected validate() {
        if (!this.selectRef) {
            return false;
        }

        return this.selectRef.checkValidity();
    }

    protected render() {
        const onValueChanged = (e: VaadinSelectChangedEvent) => {
            const { value: valueId } = exists(e.detail) ? e.detail : {};
            const item = exists(valueId) ? this.itemMap.get(valueId) : undefined;
            if (exists(item)) {
                this.vaadinInitial = valueId;
                this.changeValue(item.value);
            }
        };

        return html`
            <vaadin-select
                ${ref(select => this.selectChanged(select as VaadinSelect | undefined))}
                @value-changed=${onValueChanged}
                @change=${onValueChanged}
                class="w-full"
                part="component"
                .label=${this.label}
                .value=${this.vaadinInitial}
                .items=${this.vaadinItems}
                placeholder=${ifDefined(this.placeholder)}
                helper-text=${ifDefined(this.helperText)}
                ?required=${this.required}
                ?readonly=${this.readonly}
                ?disabled=${this.disabled}
            >
            </vaadin-select>
        `;
    }

    private selectChanged(select?: VaadinSelect) {
        this.selectRef = select;

        if (exists(select)) {
            this.performValidation();
        }
    }

    private populateVaadinItems(previous: SelectItemVm<T>[]) {
        if (!this.items || deepEquals(this.items, previous)) {
            return;
        }

        this.itemMap.clear();
        this.vaadinItems = this.items.map(i => this.mapItem(i));

        // re-calculate vaadinInitial in case items are set after initial is
        if (!exists(this.vaadinInitial) && exists(this.initial)) {
            this.populateVaadinInitial();
        }
    }

    private populateVaadinInitial(previous?: T) {
        if (!this.initial || deepEquals(this.initial, previous)) {
            return;
        }

        // find the ID of the object specified at initial
        // w/ a linear scan; select size is likely
        // small so the performance should be acceptable
        const [id] = Array.from(this.itemMap.entries())
            .filter(([, v]) => v.value === this.initial)
            .map(([k]) => k);

        if (id) {
            this.vaadinInitial = id;
        }
    }

    private mapItem(item: SelectItemVm<T>) {
        const vItem = createVaadinSelectItem(item);
        const { value: id } = vItem;

        this.itemMap.set(id, item);

        return vItem;
    }

    public static get styles() {
        return [
            ...super.styles,
            css`
                vaadin-select-value-button::part(label) {
                    position: inherit;
                    padding: 0px !important;
                }
                vaadin-select-value-button[focused]::part(label) {
                    font-size: 14px;
                }
                vaadin-select::part(input-field) {
                    border: none;
                    border-bottom: 1px solid var(--input-field-border);
                    background-color: var(--input-field-background);
                    border-radius: 4px 4px 0 0;
                    color: var(--input-field-text);
                }
            `,
        ];
    }
}

function createVaadinSelectItem<T>(i: SelectItemVm<T>) {
    const { label, disabled } = i;
    const id = i.id ? i.id.toString() : createUniqueId();

    return {
        // the value given to Vaadin will be the ID that we will use
        // to look up the real value when emitting changes to consumers
        value: id,
        label,
        disabled,
    };
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: TmSelect<unknown>;
    }
}
